import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { DiscoverInput } from '@/components/DiscoverInput';
import { AttachedMedia, AIAgent } from '@/types/aiAgents';
import { formatDistanceToNow } from 'date-fns';
import {
    fetchDiscoverContent,
    WeatherData,
    NewsItem,
    TopicCard,
    DailyDigest,
    DiscoverMode,
    ModeInsight,
} from '@/services/discoverService';
import { getRandomSuggestions } from '@/data/chatSuggestions';
import {
    Sparkles,
    TrendingUp,
    Calendar,
    Play,
    Flame,
    Clock,
    ArrowUpRight,
    MapPin,
    CheckCircle2,
} from 'lucide-react';

type DiscoverContentType = 'weather' | 'news' | 'topic' | 'digest';

interface DiscoverExperienceProps {
    isDarkMode: boolean;
    variant?: 'full' | 'preview';
    enableStickyInput?: boolean;
    className?: string;
    onContentSelect?: (type: DiscoverContentType, data: any) => void;
    onPromptSubmit?: (query: string, mode: DiscoverMode, media?: AttachedMedia | null, agent?: AIAgent | null) => Promise<void> | void;
    showInput?: boolean;
}

export const DiscoverExperience: React.FC<DiscoverExperienceProps> = ({
    isDarkMode,
    variant = 'full',
    enableStickyInput = false,
    className,
    onContentSelect,
    onPromptSubmit,
    showInput = true,
}) => {
    const navigate = useNavigate();
    const [discoverQuery, setDiscoverQuery] = useState('');
    const [inputLoading, setInputLoading] = useState(false);
    const [currentMode, setCurrentMode] = useState<DiscoverMode>('chat');
    const [attachedMedia, setAttachedMedia] = useState<AttachedMedia | null>(null);
    const [selectedAgent, setSelectedAgent] = useState<AIAgent | null>(null);
    const [weather, setWeather] = useState<WeatherData | null>(null);
    const [news, setNews] = useState<NewsItem[]>([]);
    const [topics, setTopics] = useState<TopicCard[]>([]);
    const [dailyDigest, setDailyDigest] = useState<DailyDigest | null>(null);
    const [modeInsights, setModeInsights] = useState<ModeInsight[]>([]);
    const [newsSummary, setNewsSummary] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeSource, setActiveSource] = useState<string>('All');
    const [activeTopicCategory, setActiveTopicCategory] = useState<string>('All');
    const [showStickyInput, setShowStickyInput] = useState(false);
    const mainInputRef = useRef<HTMLDivElement | null>(null);

    const applyContent = useCallback(
        (content: {
            weather: WeatherData | null;
            news: NewsItem[];
            topics: TopicCard[];
            dailyDigest: DailyDigest | null;
            modeInsights?: ModeInsight[];
            newsSummary?: string;
        }) => {
            setWeather(content.weather);
            setNews(content.news);
            setTopics(content.topics);
            setDailyDigest(content.dailyDigest);
            setModeInsights(content.modeInsights || []);
            setNewsSummary(content.newsSummary || null);
        },
        []
    );

    useEffect(() => {
        const loadDiscoverContent = async () => {
            setIsLoading(true);
            try {
                const content = await fetchDiscoverContent();
                applyContent(content);
            } catch (error) {
                console.error('Error loading discover content:', error);
            } finally {
                setTimeout(() => setIsLoading(false), 600);
            }
        };

        loadDiscoverContent();
    }, [applyContent]);

    useEffect(() => {
        if (!enableStickyInput || !showInput) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setShowStickyInput(!entry.isIntersecting);
            },
            { threshold: 0.1 }
        );

        const target = mainInputRef.current;

        if (target) {
            observer.observe(target);
        }

        return () => {
            if (target) {
                observer.unobserve(target);
            }
            observer.disconnect();
        };
    }, [enableStickyInput, showInput]);

    const handleCardClick = useCallback(
        (type: DiscoverContentType, data: any) => {
            if (onContentSelect) {
                onContentSelect(type, data);
                return;
            }

            const id = data?.id ?? Math.random().toString(36).slice(2, 10);
            navigate(`/discover/${type}/${id}`, { state: { data } });
        },
        [navigate, onContentSelect]
    );

    const handleSend = useCallback(async () => {
        if (!discoverQuery.trim() && !attachedMedia) return;

        setInputLoading(true);
        try {
            if (onPromptSubmit) {
                await onPromptSubmit(discoverQuery.trim(), currentMode, attachedMedia, selectedAgent);
            } else {
                console.debug('Discover input send:', { query: discoverQuery, mode: currentMode, media: attachedMedia, agent: selectedAgent });
            }
        } finally {
            setInputLoading(false);
            setDiscoverQuery('');
            setAttachedMedia(null);
        }
    }, [discoverQuery, currentMode, attachedMedia, selectedAgent, onPromptSubmit]);

    const handleSuggestionClick = useCallback((prompt: string) => {
        if (!prompt) return;

        // If showInput is false (preview mode), directly submit the prompt
        if (!showInput && onPromptSubmit) {
            onPromptSubmit(prompt, currentMode, null, selectedAgent);
        } else {
            // Otherwise, set it in the input field
            setDiscoverQuery(prompt);
            requestAnimationFrame(() => {
                const textarea = mainInputRef.current?.querySelector('textarea');
                if (textarea) {
                    (textarea as HTMLTextAreaElement).focus();
                }
            });
        }
    }, [mainInputRef, showInput, onPromptSubmit, currentMode]);

    const filteredNews = useMemo(() => {
        if (activeSource === 'All') return news;
        return news.filter((item) => item.source?.toLowerCase() === activeSource.toLowerCase());
    }, [news, activeSource]);

    const filteredTopics = useMemo(() => {
        if (activeTopicCategory === 'All') return topics;
        return topics.filter((item) => item.category?.toLowerCase() === activeTopicCategory.toLowerCase());
    }, [topics, activeTopicCategory]);

    const heroStats = useMemo(() => {
        const totalStories = news.length;
        const totalTopics = topics.length;
        const digestItems = dailyDigest?.items.length ?? 0;
        const freshness = news[0]?.publishedAt
            ? formatDistanceToNow(new Date(news[0].publishedAt), { addSuffix: true })
            : 'Just now';
        return [
            { label: 'Stories', value: totalStories, icon: TrendingUp },
            { label: 'Focus Areas', value: totalTopics, icon: Flame },
            { label: 'Digest Items', value: digestItems, icon: Calendar },
            { label: 'Latest Update', value: freshness, icon: Clock },
        ];
    }, [news, topics, dailyDigest]);

    const activeModeInsight = useMemo(() => {
        if (!modeInsights.length) {
            // Fallback default mode insight with random suggestions from our data
            return {
                mode: 'chat' as DiscoverMode,
                title: 'Chat Mode',
                summary: 'Ask questions, explore ideas, and get instant answers powered by AI.',
                promptSuggestions: getRandomSuggestions(4).map(s => s.text),
                tips: [],
                callToAction: 'Start exploring'
            };
        }
        return modeInsights.find((insight) => insight.mode === currentMode) || modeInsights[0];
    }, [modeInsights, currentMode]);

    const secondaryModeInsights = useMemo(() => {
        if (!modeInsights.length) return [];
        return modeInsights.filter((insight) => insight.mode !== (activeModeInsight?.mode ?? currentMode)).slice(0, 2);
    }, [modeInsights, activeModeInsight, currentMode]);

    const activePromptSuggestions = activeModeInsight?.promptSuggestions?.length
        ? activeModeInsight.promptSuggestions
        : activeModeInsight?.tips ?? [];

    const newsSources = useMemo(() => {
        const sources = Array.from(new Set(news.map((item) => item.source).filter(Boolean)));
        return ['All', ...sources];
    }, [news]);

    const topicCategories = useMemo(() => {
        const categories = Array.from(new Set(topics.map((item) => item.category).filter(Boolean)));
        return ['All', ...categories];
    }, [topics]);

    const getLiquidCardClasses = useCallback(
        (options?: { interactive?: boolean; padding?: string }) => {
            const interactive = options?.interactive ?? false;
            const padding = options?.padding ?? 'p-6';
            return cn(
                'relative overflow-hidden rounded-3xl border bg-transparent transition-all duration-300',
                padding,
                interactive && 'cursor-pointer hover:-translate-y-1',
                isDarkMode
                    ? 'border-[#8b8b8b] text-white shadow-[0_18px_45px_rgba(12,15,24,0.35)]'
                    : 'border-slate-200/70 text-slate-900 shadow-[0_12px_30px_rgba(15,23,42,0.12)]',
                interactive &&
                (isDarkMode
                    ? 'hover:shadow-[0_32px_88px_rgba(12,15,24,0.6)]'
                    : 'hover:shadow-[0_28px_72px_rgba(15,23,42,0.2)]')
            );
        },
        [isDarkMode]
    );

    const getWeatherArtForCondition = useCallback((condition: string) => {
        const c = (condition || '').toLowerCase();
        if (c.includes('thunder') || c.includes('storm')) return '/weather/thunderstorm.png';
        if (c.includes('rain') || c.includes('drizzle') || c.includes('shower')) return '/weather/rain.png';
        if ((c.includes('cloud') && c.includes('sun')) || c.includes('partly')) return '/weather/partly-cloudy.png';
        if (c.includes('cloud') || c.includes('overcast')) return '/weather/cloudy.png';
        if (c.includes('sun') || c.includes('clear')) return '/weather/sunny.png';
        return '/weather/partly-cloudy.png';
    }, []);

    const renderLiquidOverlay = useCallback(
        (accent: 'neutral' | 'sky' | 'rose' | 'emerald' | 'violet' = 'neutral') => {
            const lightAccentMap = {
                neutral: 'from-white via-white to-white',
                sky: 'from-sky-200 via-white to-cyan-100',
                rose: 'from-rose-200 via-white to-amber-100',
                emerald: 'from-emerald-200 via-white to-teal-100',
                violet: 'from-violet-200 via-white to-blue-100',
            } as const;

            const darkAccentMap = {
                neutral: 'from-[#1c1c1c] via-[#222222] to-[#1c1c1c]',
                sky: 'from-[#1f2a30] via-[#222629] to-[#1e2427]',
                rose: 'from-[#2b1f20] via-[#262222] to-[#2a1f1d]',
                emerald: 'from-[#1f2b26] via-[#222725] to-[#1e2923]',
                violet: 'from-[#231f2b] via-[#242227] to-[#201f28]',

            } as const;

            const gradientClass = (isDarkMode ? darkAccentMap : lightAccentMap)[accent] ?? (isDarkMode ? darkAccentMap.neutral : lightAccentMap.neutral);

            return (
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div className={cn('absolute inset-0 bg-gradient-to-br opacity-70', gradientClass)} />
                    <div
                        className={cn(
                            'absolute -top-1/3 left-1/2 h-48 w-72 -translate-x-1/2 rotate-[18deg] rounded-[45%] opacity-40 mix-blend-screen',
                            isDarkMode ? 'bg-white/12' : 'bg-white/30'
                        )}
                    />
                    <div
                        className={cn(
                            'absolute bottom-[-28%] right-[-6%] h-60 w-60 rounded-full opacity-30',
                            isDarkMode ? 'bg-white/8' : 'bg-white/20'
                        )}
                    />
                </div>
            );
        },
        [isDarkMode]
    );

    const displayNews = useMemo(() => {
        if (variant === 'preview') {
            return filteredNews.slice(0, 2);
        }
        return filteredNews;
    }, [filteredNews, variant]);

    const displayTopics = useMemo(() => {
        if (variant === 'preview') {
            return filteredTopics.slice(0, 3);
        }
        return filteredTopics;
    }, [filteredTopics, variant]);

    const [columnCount, setColumnCount] = useState(() => {
        if (typeof window === 'undefined') return 1;
        if (window.innerWidth >= 1024) return 3;
        if (window.innerWidth >= 640) return 2;
        return 1;
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const getColumnCount = () => {
            if (window.innerWidth >= 1024) return 3;
            if (window.innerWidth >= 640) return 2;
            return 1;
        };

        const handleResize = () => {
            setColumnCount((prev) => {
                const next = getColumnCount();
                return prev === next ? prev : next;
            });
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const newsColumns = useMemo(() => {
        const count = Math.max(columnCount, 1);
        const columns = Array.from({ length: count }, () => [] as any[]);
        displayNews.forEach((item, index) => {
            columns[index % count].push(item);
        });
        return columns;
    }, [displayNews, columnCount]);

    const topicsColumns = useMemo(() => {
        const count = Math.max(columnCount, 1);
        const columns = Array.from({ length: count }, () => [] as any[]);
        displayTopics.forEach((item, index) => {
            columns[index % count].push(item);
        });
        return columns;
    }, [displayTopics, columnCount]);

    const ShimmerCard = ({ className }: { className?: string }) => (
        <div className={cn(getLiquidCardClasses({ padding: 'p-0' }), className)}>
            {renderLiquidOverlay()}
            <div className="relative z-10 h-full w-full overflow-hidden">
                <div
                    className={cn(
                        'absolute inset-0 animate-[shimmer_2.4s_linear_infinite]',
                        isDarkMode
                            ? 'bg-gradient-to-r from-transparent via-white/80 to-transparent'
                            : 'bg-gradient-to-r from-transparent via-white/30 to-transparent'
                    )}
                />
            </div>
            <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .shimmer-animation {
          animation: shimmer 2s infinite;
        }
      `}</style>
        </div>
    );

    return (
        <div className={cn('relative w-full', className)}>
            <style>{`
              @keyframes floatWeatherArt {
                0% { transform: translate3d(-4px, 0, 0) rotate(0deg) scale(1); }
                50% { transform: translate3d(4px, -5px, 0) rotate(0.6deg) scale(1.015); }
                100% { transform: translate3d(-4px, 0, 0) rotate(0deg) scale(1); }
              }
              .animate-float { animation: floatWeatherArt 8s ease-in-out infinite; }
            `}</style>


            <section className="mb-12">
                <div className="flex flex-wrap gap-6 px-0 w-full">
                    {isLoading ? (
                        <>
                            <ShimmerCard className="h-48 flex-1 min-w-[280px]" />
                            <ShimmerCard className="h-48 flex-1 min-w-[280px]" />
                            <ShimmerCard className="h-48 flex-1 min-w-[280px]" />
                        </>
                    ) : (
                        <>
                            {/* Weather Card */}
                            {weather && (
                                <div
                                    onClick={() => handleCardClick('weather', weather)}
                                    className={cn(getLiquidCardClasses({ interactive: true }), 'h-[max-content] flex-1 min-w-[280px]')}
                                    style={{
                                        backgroundImage: 'url(/src/assets/discoverPage/CardBG2.png)',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                    }}
                                >
                                    {renderLiquidOverlay('sky')}
                                    <div className="relative z-10">
                                        <img
                                            src={getWeatherArtForCondition(weather.condition)}
                                            alt={weather.condition}
                                            className={cn(
                                                'pointer-events-none absolute -top-6 right-0 w-32 sm:w-40 md:w-48 opacity-80 animate-float',
                                                isDarkMode ? 'mix-blend-screen' : 'mix-blend-multiply'
                                            )}
                                        />
                                        <div className="relative z-10 flex items-start justify-between">
                                            <div>
                                                <div className="mt-1 flex items-center gap-2 text-xl font-semibold">
                                                    <MapPin className="h-4 w-4" />
                                                    {weather.location}
                                                </div>
                                                {weather.region && weather.country && (
                                                    <div className={cn('text-xs mt-1 opacity-60', isDarkMode ? 'text-slate-300' : 'text-slate-600')}>
                                                        {weather.region}, {weather.country}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2" />
                                        </div>
                                        <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <div className={cn('text-xs uppercase opacity-70', isDarkMode ? 'text-slate-300' : 'text-slate-500')}>
                                                    Condition
                                                </div>
                                                <div className="text-base font-semibold">{weather.condition}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className={cn('text-xs uppercase opacity-70', isDarkMode ? 'text-slate-300' : 'text-slate-500')}>
                                                    Current
                                                </div>
                                                <div className="text-3xl font-semibold">{weather.temperature}°</div>
                                            </div>
                                            <div>
                                                <div className={cn('text-xs uppercase opacity-70', isDarkMode ? 'text-slate-300' : 'text-slate-500')}>
                                                    High
                                                </div>
                                                <div className="text-base font-semibold">{weather.high}°</div>
                                            </div>
                                            <div className="text-right">
                                                <div className={cn('text-xs uppercase opacity-70', isDarkMode ? 'text-slate-300' : 'text-slate-500')}>
                                                    Low
                                                </div>
                                                <div className="text-base font-semibold">{weather.low}°</div>
                                            </div>
                                            {weather.humidity !== undefined && (
                                                <div>
                                                    <div className={cn('text-xs uppercase opacity-70', isDarkMode ? 'text-slate-300' : 'text-slate-500')}>
                                                        Humidity
                                                    </div>
                                                    <div className="text-base font-semibold">{weather.humidity}%</div>
                                                </div>
                                            )}
                                            {weather.windSpeed !== undefined && (
                                                <div className="text-right">
                                                    <div className={cn('text-xs uppercase opacity-70', isDarkMode ? 'text-slate-300' : 'text-slate-500')}>
                                                        Wind
                                                    </div>
                                                    <div className="text-base font-semibold">{weather.windSpeed} km/h {weather.windDirection}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Daily Digest Card */}
                            {dailyDigest && (
                                <div
                                    onClick={() => handleCardClick('digest', dailyDigest)}
                                    className={cn(getLiquidCardClasses({ interactive: true }), 'h-[max-content] flex-1 min-w-[280px]')}
                                    style={{
                                        backgroundImage: 'url(/src/assets/discoverPage/CardBG1.png)',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                    }}
                                >
                                    {renderLiquidOverlay('rose')}
                                    <div className="relative z-10 flex h-full flex-col">
                                        <div className="flex items-center justify-between">
                                            <div className={cn('text-xs uppercase opacity-80', isDarkMode ? 'text-rose-200' : 'text-rose-500')}>
                                                Daily Digest
                                            </div>
                                            <div className={cn('mt-2 text-sm opacity-80', isDarkMode ? 'text-slate-200' : 'text-slate-600')}>
                                                {dailyDigest.date}
                                            </div>
                                        </div>
                                        <h3 className={cn('mt-3 text-xl font-semibold', isDarkMode ? 'text-white' : 'text-slate-900')}>
                                            {dailyDigest.title}
                                        </h3>
                                        <div className="mt-4 space-y-3 text-sm">
                                            {dailyDigest.items.slice(0, variant === 'preview' ? 3 : 4).map((digestItem: string, index: number) => (
                                                <div key={index} className="flex items-start gap-3">
                                                    <span className="leading-snug opacity-90">{digestItem}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Mode Insights Card */}
                            <div className={cn(getLiquidCardClasses(), 'h-[max-content] flex-1 min-w-[280px]')} style={{
                                backgroundImage: 'url(/src/assets/discoverPage/CardBG3.png)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                            }}>
                                {renderLiquidOverlay('violet')}
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-xs uppercase tracking-wide opacity-70">Prompt ideas</div>
                                            <div className="text-md font-semibold capitalize">{activeModeInsight.title}</div>
                                        </div>
                                    </div>
                                    {activeModeInsight.summary && (
                                        <p className={cn('mt-3 text-sm leading-relaxed opacity-80', isDarkMode ? 'text-slate-200' : 'text-[#1c1c1c]')}>
                                            {activeModeInsight.summary}
                                        </p>
                                    )}
                                    {activePromptSuggestions.length > 0 && (
                                        <div className="mt-4 flex flex-col gap-2">
                                            {activePromptSuggestions.map((suggestion, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleSuggestionClick(suggestion)}
                                                    className={cn(
                                                        'group flex items-center justify-between rounded-2xl px-4 py-3 text-left text-sm transition-all duration-200',
                                                        isDarkMode
                                                            ? 'bg-white/5 text-slate-200 hover:bg-white/10'
                                                            : 'bg-slate-100 text-slate-800 hover:bg-slate-200'
                                                    )}
                                                >
                                                    <span className="pr-3 leading-snug">{suggestion}</span>
                                                    <ArrowUpRight className="h-4 w-4 flex-shrink-0 opacity-60 transition-opacity group-hover:opacity-100" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>

            {showInput && (
                <div className={cn('space-y-6', variant === 'full' ? 'mb-10' : 'mb-6')} ref={mainInputRef}>
                    <DiscoverInput
                        value={discoverQuery}
                        onChange={setDiscoverQuery}
                        onSend={handleSend}
                        isDarkMode={isDarkMode}
                        isLoading={inputLoading}
                        selectedMode={currentMode}
                        onModeChange={(mode) => setCurrentMode(mode as DiscoverMode)}
                        attachedMedia={attachedMedia}
                        onMediaAttach={setAttachedMedia}
                        selectedAgent={selectedAgent}
                        onAgentChange={setSelectedAgent}
                    />
                </div>
            )}

            <section className={variant === 'full' ? 'mb-12' : 'mb-8'}>
                <div className={cn(
                    'mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-start',
                    variant === 'preview' && 'sm:flex-col sm:items-start'
                )}>
                    {variant === 'full' && (
                        <div className="flex flex-wrap gap-2">
                            {newsSources.map((source) => (
                                <button
                                    key={source}
                                    onClick={() => setActiveSource(source)}
                                    className={cn(
                                        'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                                        activeSource === source
                                            ? isDarkMode
                                                ? 'border-white/40 bg-white/10 text-white'
                                                : 'border-sky-400 bg-sky-100 text-sky-800'
                                            : isDarkMode
                                                ? 'border-white/10 text-slate-300 hover:border-white/30'
                                                : 'border-slate-200 text-grey-600 hover:border-slate-400'
                                    )}
                                >
                                    {source}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-0 w-full">
                    {newsColumns.map((column, columnIndex) => (
                        <div key={`news-column-${columnIndex}`} className="flex flex-col gap-6">
                            {isLoading ? (
                                Array.from({ length: variant === 'preview' ? 2 : 4 }).map((_, index) => (
                                    <ShimmerCard key={index} className="h-64" />
                                ))
                            ) : column.length > 0 ? (
                                column.map((item) => (
                                    <div
                                        key={item.id}
                                        onClick={() => handleCardClick('news', item)}
                                        className={cn(getLiquidCardClasses({ interactive: true, padding: 'p-0' }), 'group h-[max-content] rounded-3xl overflow-hidden')}
                                        style={{
                                            backgroundImage: 'url(/src/assets/discoverPage/CardBg.png)',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            backgroundRepeat: 'no-repeat',
                                        }}
                                    >
                                        {renderLiquidOverlay('neutral')}
                                        <div className="relative z-10 flex h-full flex-col overflow-hidden rounded-3xl">
                                            {item.image && (
                                                <div className="relative h-48 overflow-hidden rounded-t-3xl">
                                                    <img
                                                        src={item.image}
                                                        alt={item.title}
                                                        className="h-full w-full object-cover transition-transform duration-500"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                                                    <div className="absolute bottom-4 left-4 flex items-center gap-2 text-xs text-white/85">
                                                        <span className="rounded-full bg-white/25 px-3 py-1 backdrop-blur-sm">{item.source}</span>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="space-y-4 p-6">
                                                <h4 className="text-lg font-semibold leading-tight">{item.title}</h4>
                                                <p className="text-sm opacity-80">{item.description}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : columnIndex === 0 && displayNews.length === 0 ? (
                                <div className={cn(getLiquidCardClasses({ padding: 'p-10' }))}>
                                    {renderLiquidOverlay('neutral')}
                                    <div className="relative z-10 text-center text-sm opacity-80">
                                        No stories for this source yet. Try another filter or refresh the feed.
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>
            </section>

            <section className={variant === 'preview' ? '' : 'mb-12'}>
                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h3 className={cn(
                            variant === 'full' ? 'text-2xl font-bold' : 'text-xl font-semibold',
                            isDarkMode ? 'text-white' : 'text-slate-900'
                        )}>
                            Deep dives & focus areas
                        </h3>
                    </div>

                    {variant === 'full' && (
                        <div className="flex flex-wrap gap-2">
                            {topicCategories.map((category) => (
                                <button
                                    key={category}
                                    onClick={() => setActiveTopicCategory(category)}
                                    className={cn(
                                        'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                                        activeTopicCategory === category
                                            ? isDarkMode
                                                ? 'border-white/40 bg-white/10 text-white'
                                                : 'border-emerald-300 bg-emerald-100 text-emerald-800'
                                            : isDarkMode
                                                ? 'border-white/10 text-slate-300 hover:border-white/30'
                                                : 'border-grey-200 text-grey-600 hover:border-grey-400'
                                    )}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-0 w-full">
                    {topicsColumns.map((column, columnIndex) => (
                        <div key={`topics-column-${columnIndex}`} className="flex flex-col gap-6">
                            {isLoading ? (
                                Array.from({ length: variant === 'preview' ? 3 : 6 }).map((_, index) => (
                                    <ShimmerCard key={index} className="h-56" />
                                ))
                            ) : column.length > 0 ? (
                                column.map((topic) => (
                                    <div
                                        key={topic.id}
                                        onClick={() => handleCardClick('topic', topic)}
                                        className={cn(getLiquidCardClasses({ interactive: true, padding: 'p-0' }), 'group h-[max-content] rounded-3xl overflow-hidden')}
                                        style={{
                                            backgroundImage: 'url(/src/assets/discoverPage/CardBg.png)',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            backgroundRepeat: 'no-repeat',
                                        }}
                                    >
                                        {renderLiquidOverlay('emerald')}
                                        <div className="relative z-10 flex h-full flex-col overflow-hidden rounded-3xl">
                                            {topic.image && (
                                                <div className="relative h-40 overflow-hidden rounded-t-3xl">
                                                    <img
                                                        src={topic.image}
                                                        alt={topic.title}
                                                        className="h-full w-full object-cover transition-transform duration-500"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                                                    <span className="absolute bottom-2 left-2 rounded-full bg-white/25 px-3 py-1 text-xs font-semibold backdrop-blur-sm">
                                                        {topic.category}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="space-y-3 p-6">
                                                <div className="text-xs uppercase tracking-wide opacity-70">Featured insight</div>
                                                <h4 className="text-lg font-semibold leading-tight">{topic.title}</h4>
                                                <p className="text-sm opacity-80">{topic.description}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : columnIndex === 0 && displayTopics.length === 0 ? (
                                <div className={cn(getLiquidCardClasses({ padding: 'p-10' }))}>
                                    {renderLiquidOverlay('emerald')}
                                    <div className="relative z-10 text-center text-sm opacity-80">
                                        No topics in this category yet. Try switching filters or refresh the feed.
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>
            </section>

            {showInput && enableStickyInput && showStickyInput && (
                <div className="fixed bottom-1 left-0 right-0 z-50 px-1 xs:px-2 sm:px-6 lg:px-8">
                    <div className="mx-auto max-w-4xl">
                        <DiscoverInput
                            value={discoverQuery}
                            onChange={setDiscoverQuery}
                            onSend={handleSend}
                            isDarkMode={isDarkMode}
                            isLoading={inputLoading}
                            selectedMode={currentMode}
                            onModeChange={(mode) => setCurrentMode(mode as DiscoverMode)}
                            attachedMedia={attachedMedia}
                            onMediaAttach={setAttachedMedia}
                            selectedAgent={selectedAgent}
                            onAgentChange={setSelectedAgent}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
